import React from 'react'
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
  CircularProgress,
} from '@mui/material'
import { formatBudgetLeft, formatEstimateMoney } from '../../utils/themeAICost'
import type { MoneyUnit } from '../../tokenbudgets/model'
import { junkEstimateLabel } from '../../utils/themeAIJunk'

export interface CostConfirmDetails {
  responseCount: number
  wordCount: number
  themeCount: number
  estimatedTokens: number
  estimatedCost: number
  remainingBudget?: number
  /** Credits for a non-Manager (themeAIReaderUnit); absent for a Manager, who reads pounds. */
  moneyUnit?: MoneyUnit
  /** A fixed-mode list price: "11 × 100 responses". */
  priceBasis?: string
  humanComparisonText?: string
  humanCostGBP?: number
  humanDays?: number
  /** Answers a saved junk decision keeps out of the run (#1891). */
  junkExcluded?: number
  /** What the saved junk decision is, when the estimate applied one. */
  junkDecision?: 'keep' | 'drop' | 'code' | ''
  /** false while the server estimate (Wave 1C) isn't deployed yet — the
   * dialog still renders the client-side fallback estimate, just labelled. */
  serverEstimateAvailable: boolean
}

interface CostConfirmDialogProps {
  open: boolean
  loading: boolean
  details: CostConfirmDetails | null
  onCancel: () => void
  onProceed: () => void
}

// Pre-processing confirmation dialog — design spec #916 §7.3.
const CostConfirmDialog: React.FC<CostConfirmDialogProps> = ({ open, loading, details, onCancel, onProceed }) => {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Ready to code</DialogTitle>
      <DialogContent>
        {loading || !details ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={1.5}>
            <Row label="Responses to code" value={details.responseCount.toLocaleString()} />
            {Boolean(details.junkExcluded) && (
              <Row label={junkEstimateLabel(details.junkDecision)} value={details.junkExcluded!.toLocaleString()} />
            )}
            <Row label="Estimated words" value={`~${details.wordCount.toLocaleString()}`} />
            <Row label="Code frame" value={`${details.themeCount} theme${details.themeCount === 1 ? '' : 's'}`} />
            <Row label="Estimated tokens" value={`~${details.estimatedTokens.toLocaleString()}`} />
            <Row label="Estimated budget spend" value={formatEstimateMoney(details.estimatedCost, details.moneyUnit)} emphasize />
            {details.priceBasis && <Row label="Price list" value={details.priceBasis} />}
            {typeof details.remainingBudget === 'number' && (
              <Row label="Budget left" value={formatBudgetLeft(details.remainingBudget, details.moneyUnit)} />
            )}
            <Divider sx={{ my: 1 }} />
            {typeof details.humanDays === 'number' && typeof details.humanCostGBP === 'number' ? (
              <Box sx={(theme) => ({ p: 1.5, borderRadius: 1.5, backgroundColor: alpha(theme.palette.success.main, 0.08) })}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Time saved: about {formatDays(details.humanDays)} of human coding
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Coding these responses by hand, with a code frame and a quality check, {details.moneyUnit ? `would take about ${formatDays(details.humanDays)}.` : `would cost about £${Math.round(details.humanCostGBP).toLocaleString()}.`}
                </Typography>
              </Box>
            ) : details.humanComparisonText ? (
              <Typography variant="body2" color="text.secondary">
                {details.humanComparisonText}
              </Typography>
            ) : null}
            {!details.serverEstimateAvailable && (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                This is a client-side estimate — precise server-side estimation isn't deployed yet.
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} color="inherit">Cancel</Button>
        <Button variant="contained" color="info" disabled={loading || !details} onClick={onProceed}>
          Proceed
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const formatDays = (days: number) => {
  if (days < 1) return 'half a day'
  if (days < 5) return `${Math.round(days)} day${Math.round(days) === 1 ? '' : 's'}`
  const weeks = Math.round(days / 5)
  return `${weeks} week${weeks === 1 ? '' : 's'}`
}

const Row: React.FC<{ label: string; value: string; emphasize?: boolean }> = ({ label, value, emphasize }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: emphasize ? 700 : 500 }}>{value}</Typography>
  </Box>
)

export default CostConfirmDialog
